import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Save, ChevronLeft, Eye, Search } from "lucide-react";
import { api } from "../../api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import MarkdownView from "../../components/MarkdownView";
import { useToast } from "../../hooks/use-toast";

const EMPTY = {
  slug: "", title: "", body: "", published: true,
  seo: { title: "", description: "", keywords: "", ogImage: "", canonical: "", noindex: false, jsonLd: "" },
};

export default function PageEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const { toast } = useToast();
  const [page, setPage] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/pages/${id}`);
        setPage({ ...EMPTY, ...data, seo: { ...EMPTY.seo, ...(data.seo || {}) } });
      } catch { toast({ title: "Failed to load" }); }
      finally { setLoading(false); }
    })();
  }, [id, isNew]);

  const onChange = (k, v) => setPage((p) => ({ ...p, [k]: v }));
  const onSeo = (k, v) => setPage((p) => ({ ...p, seo: { ...p.seo, [k]: v } }));

  const save = async () => {
    if (!page.title) { toast({ title: "Title required" }); return; }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/admin/pages", page);
        toast({ title: "Page created" });
        navigate(`/admin/pages/${data.id}`);
      } else {
        await api.put(`/admin/pages/${id}`, page);
        toast({ title: "Page saved" });
      }
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-400">Loading page...</div>;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/pages" className="text-sm text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Back to pages</Link>
          <h1 className="text-2xl md:text-3xl font-bold mt-1">{isNew ? "New Page" : "Edit Page"}</h1>
        </div>
        <div className="flex gap-2">
          {!isNew && page.slug && <a href={`/p/${page.slug}`} target="_blank" rel="noreferrer" className="btn-outline-gold px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1.5"><Eye className="w-4 h-4" /> Preview</a>}
          <Button onClick={save} disabled={saving} className="btn-gold border-0 px-5 py-2.5 rounded-lg"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save"}</Button>
        </div>
      </div>

      <Tabs defaultValue="content" className="mt-6">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-5">
          <div className="grid lg:grid-cols-[1fr_280px] gap-5">
            <div className="card-dark p-5 space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={page.title} onChange={(e) => onChange("title", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
              </div>
              <div>
                <Label>Slug</Label>
                <Input value={page.slug} onChange={(e) => onChange("slug", e.target.value)} placeholder="auto-generated" className="mt-1.5 bg-[#0c1530] border-white/10 text-white font-mono text-sm" />
                <div className="text-[11px] text-slate-500 mt-1">URL: /p/{page.slug || "your-slug"}</div>
              </div>
              <div>
                <Label>Body (Markdown)</Label>
                <Textarea rows={20} value={page.body} onChange={(e) => onChange("body", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white font-mono text-sm" />
              </div>
            </div>
            <div className="card-dark p-5">
              <Label>Published</Label>
              <div className="mt-2 flex items-center gap-2">
                <Switch checked={page.published} onCheckedChange={(v) => onChange("published", v)} />
                <span className="text-sm text-slate-300">{page.published ? "Live" : "Draft"}</span>
              </div>
              <div className="mt-4 text-[12px] text-slate-400 leading-relaxed">
                Pages render at <code className="text-amber-300">/p/{page.slug || "slug"}</code> with markdown body + full SEO meta. Add them to nav by editing your <Link to="/admin/settings" className="text-amber-400">Site Settings</Link>.
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="seo" className="mt-5">
          <div className="card-dark p-5 space-y-4">
            <div>
              <Label>SEO Title</Label>
              <Input value={page.seo.title} onChange={(e) => onSeo("title", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
              <div className="text-[11px] text-slate-500 mt-1">{(page.seo.title || "").length} / 60 chars recommended</div>
            </div>
            <div>
              <Label>Meta Description</Label>
              <Textarea rows={2} value={page.seo.description} onChange={(e) => onSeo("description", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
              <div className="text-[11px] text-slate-500 mt-1">{(page.seo.description || "").length} / 160 chars recommended</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Focus Keywords</Label>
                <Input value={page.seo.keywords} onChange={(e) => onSeo("keywords", e.target.value)} placeholder="comma, separated" className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
              </div>
              <div>
                <Label>Canonical URL</Label>
                <Input value={page.seo.canonical} onChange={(e) => onSeo("canonical", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
              </div>
            </div>
            <div>
              <Label>OG Image URL</Label>
              <Input value={page.seo.ogImage} onChange={(e) => onSeo("ogImage", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={page.seo.noindex} onCheckedChange={(v) => onSeo("noindex", v)} />
              <span className="text-sm text-slate-300">No-index</span>
            </div>
            <div>
              <Label>JSON-LD Schema</Label>
              <Textarea rows={5} value={page.seo.jsonLd} onChange={(e) => onSeo("jsonLd", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white font-mono text-xs" />
            </div>
            <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
              <div className="text-xs text-slate-400 mb-2 flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Google Preview</div>
              <div className="text-blue-400 text-lg leading-tight">{page.seo.title || page.title || "Page title"}</div>
              <div className="text-green-500 text-xs">{window.location.origin}/p/{page.slug || "slug"}</div>
              <div className="text-slate-300 text-sm mt-1">{page.seo.description || "Description appears here..."}</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="preview" className="mt-5">
          <div className="card-dark p-6">
            <h2 className="text-3xl font-bold text-white">{page.title}</h2>
            <div className="mt-5"><MarkdownView>{page.body}</MarkdownView></div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
